import React from 'react';
import { Paper, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DailyCount, MenuItemClick } from '../models/Statistics';

interface KpiCardProps {
    label: string;
    value: string | number;
    subValue?: string;
    loading?: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, subValue, loading }) => (
    <Paper elevation={2} className="p-5 flex flex-col gap-1 min-w-0">
        <div className="text-sm text-utility-mutedText font-medium uppercase tracking-wide truncate">{label}</div>
        {loading ? (
            <Skeleton variant="text" width={80} height={36} />
        ) : (
            <div className="text-3xl font-bold text-primary-dark leading-tight">{value}</div>
        )}
        {subValue && !loading && (
            <div className="text-xs text-utility-mutedText truncate">{subValue}</div>
        )}
    </Paper>
);

interface Props {
    pageViews: DailyCount[];
    prevPageViews?: DailyCount[];
    menuItemClicks: MenuItemClick[];
    period: 'last7days' | 'last30days';
    loading?: boolean;
}

const KpiCards: React.FC<Props> = ({ pageViews, prevPageViews, menuItemClicks, period, loading }) => {
    const { t } = useTranslation();

    const totalVisitors = pageViews.reduce((sum, d) => sum + d.count, 0);
    const prevTotal = prevPageViews?.reduce((sum, d) => sum + d.count, 0) ?? null;
    const days = period === 'last7days' ? 7 : 30;
    const avgPerDay = pageViews.length > 0 ? Math.round(totalVisitors / days) : 0;

    const peakDay = pageViews.reduce<DailyCount | null>((max, d) => {
        if (!max || d.count > max.count) return d;
        return max;
    }, null);

    const topItem = menuItemClicks.length > 0
        ? menuItemClicks.reduce((max, item) => item.count > max.count ? item : max)
        : null;

    let trendLabel: string | undefined;
    if (prevTotal !== null && prevTotal > 0) {
        const diff = Math.round(((totalVisitors - prevTotal) / prevTotal) * 100);
        trendLabel = diff >= 0 ? `+${diff}% ${t('statistics.kpi_vs_prev')}` : `${diff}% ${t('statistics.kpi_vs_prev')}`;
    }

    const formatPeakDate = (raw: string): string => {
        // GA4 date format: "20260318" → "2026-03-18 (Péntek)"
        if (!raw || raw.length !== 8) return raw;
        const iso = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
        const date = new Date(iso);
        const dayName = new Intl.DateTimeFormat('hu-HU', { weekday: 'long' }).format(date);
        const capitalised = dayName.charAt(0).toUpperCase() + dayName.slice(1);
        return `${iso} (${capitalised})`;
    };

    const topItemTitle = topItem
        ? (topItem.item?.translations?.find(tr => tr.title)?.title ?? topItem.item?.translations?.[0]?.title ?? `#${topItem.item_id}`)
        : '-';

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <KpiCard
                label={t('statistics.kpi_total_visitors')}
                value={totalVisitors.toLocaleString()}
                subValue={trendLabel}
                loading={loading}
            />
            <KpiCard
                label={t('statistics.kpi_avg_per_day')}
                value={avgPerDay.toLocaleString()}
                loading={loading}
            />
            <KpiCard
                label={t('statistics.kpi_peak_day')}
                value={peakDay ? peakDay.count.toLocaleString() : '-'}
                subValue={peakDay ? formatPeakDate(peakDay.date) : undefined}
                loading={loading}
            />
            <KpiCard
                label={t('statistics.kpi_top_item')}
                value={topItem ? topItem.count.toLocaleString() : '-'}
                subValue={topItem ? topItemTitle : undefined}
                loading={loading}
            />
        </div>
    );
};

export default KpiCards;
